import type { FoxItPDFForm } from '@/FoxItTypes'
import { useTexasRequiredFieldsProvider } from '@/components/documents/Foxit/composables/TexasRequiredFieldsProvider'
import {
	PdfFieldFlag,
	useDefaultRequiredFieldsProvider,
} from '@/data/requiredFormFields'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'

export type RequiredFieldsProvider = ReturnType<
	typeof useDefaultRequiredFieldsProvider
>

// Picks the required fields provider for the funeral home's state. States without
// their own provider fall back to the requirements shared by every state.
export const useRequiredFieldsProvider = (): RequiredFieldsProvider => {
	const sessionStore = useSessionStore()

	switch (sessionStore.activeFh?.billingAddress?.stateCode) {
		case 'TX':
			return useTexasRequiredFieldsProvider()
		default:
			return useDefaultRequiredFieldsProvider()
	}
}

// Flags the fields Sales Studio requires on top of the ones the PDF already marks
// required, so an unfilled one is listed in the validation modal before finalizing.
export const setAdditionalRequiredFields = (pdfForm: FoxItPDFForm) => {
	const sessionDocumentsStore = useSessionDocumentsStore()

	const additionalRequiredFields =
		useRequiredFieldsProvider().getRequiredFieldsConfig(
			sessionDocumentsStore.currentDocument?.meta.type
		)

	additionalRequiredFields.forEach(({ name, flag }) => {
		const field = pdfForm.getField(name)

		if (!field) {
			return
		}

		// Only the required bit is toggled so the field keeps its other flags
		const flags = field.getFlags()
		field.setFlags(
			flag === PdfFieldFlag.Required
				? flags | PdfFieldFlag.Required
				: flags & ~PdfFieldFlag.Required
		)
	})
}
